import React, { useMemo, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Trophy, Medal, Star, ShareNetwork, ArrowRight, Check } from '@phosphor-icons/react';
import { shareSeasonRecap } from '../utils/seasonRecap';
import { ConfettiBurst } from './ConfettiBurst';

/**
 * Season-end celebration modal:
 *   - Shows league rank, trophies won
 *   - Shows MVPs + TOTY slots where player qualified
 *   - "Share Season Recap" button → downloads 1080×1920 Story image
 *   - "Continue to Next Season" button → triggers onContinue()
 */
export const SeasonEndModal = ({ open, onClose, onContinue, career, intlPools }) => {
  const data = useMemo(() => {
    if (!open || !career) return null;
    const team = career.teams.find(t => t.id === career.playerTeamId);
    const player = team?.players?.find(p => p.id === career.playerId);
    if (!team || !player) return null;

    const seasonMatches = (career.matchHistory || []).filter(m => m.season === (career.season || 1));
    const goals = seasonMatches.reduce((s, m) => s + (m.playerGoals || 0), 0);
    const assists = seasonMatches.reduce((s, m) => s + (m.playerAssists || 0), 0);
    const avgRating = seasonMatches.length ? (seasonMatches.reduce((s, m) => s + (m.playerRating || 0), 0) / seasonMatches.length) : 0;

    // Compute league final rank
    const sorted = [...career.teams]
      .map(t => ({ ...t, gd: (t.stats?.goalsFor || 0) - (t.stats?.goalsAgainst || 0) }))
      .sort((a, b) => (b.stats?.points || 0) - (a.stats?.points || 0) || b.gd - a.gd);
    const finalRank = sorted.findIndex(t => t.id === team.id) + 1;

    // Trophies
    const trophies = [];
    if (finalRank === 1) trophies.push({ label: 'EWFL League Champions', color: '#FFD700', icon: '🏆' });
    if (career.competitions?.cup?.won) trophies.push({ label: "Emperor's Cup Winners", color: '#FF006E', icon: '🏆' });
    if (career.competitions?.ucl?.won) trophies.push({ label: 'Asian Cup Winners', color: '#CCFF00', icon: '🏆' });
    if (career.competitions?.uel?.won) trophies.push({ label: 'Asian Cup Winners', color: '#CCFF00', icon: '🏆' });
    if (career.competitions?.uecl?.won) trophies.push({ label: 'Asian Cup Winners', color: '#CCFF00', icon: '🏆' });
    if (career.competitions?.cwc?.won) trophies.push({ label: 'Club World Cup Winners', color: '#2E5AAC', icon: '🏆' });

    // Compute MVP / TOTY for current player across all comps
    const allTeams = [
      ...career.teams,
      ...(intlPools?.uclTeams || []),
      ...(intlPools?.uelTeams || []),
      ...(intlPools?.ueclTeams || []),
      ...(intlPools?.cwcTeams || []),
    ];
    const allPlayers = allTeams.flatMap(t => (t.players || []).map(p => ({ ...p, teamName: t.name })));
    const comps = ['league','cup','championsLeague','europaLeague','conferenceLeague','cwc','worldCup'];
    const labelFor = (c) => ({ league: 'League', cup: 'Cup', championsLeague: 'Asian', europaLeague: 'Asian', conferenceLeague: 'Asian', cwc: 'CWC', worldCup: 'WC', asianCup: 'Asian' }[c] || c);
    const mvpComps = {}, totyComps = {};
    comps.forEach(compKey => {
      const scored = allPlayers
        .map(p => {
          const s = p.stats?.[compKey] || {};
          const apps = s.matchesPlayed || 0;
          const g = s.goals || 0, a = s.assists || 0, cs = s.cleanSheets || 0;
          return { ...p, score: g * 3 + a * 2 + apps * 0.2 + cs * 2 };
        })
        .filter(p => p.score > 0)
        .sort((a, b) => b.score - a.score);
      if (scored[0]?.id === player.id) mvpComps[labelFor(compKey)] = true;
      // TOTY eligibility — player in top 11 of their position bucket by score
      const inBucket = (positions, count) => scored.filter(p => positions.includes(p.position)).slice(0, count);
      const toty = [
        ...inBucket(['GK'], 1),
        ...inBucket(['CB','LB','RB','LWB','RWB'], 4),
        ...inBucket(['CM','CDM','CAM','LM','RM'], 3),
        ...inBucket(['ST','CF','LW','RW','LF','RF'], 3),
      ];
      if (toty.length >= 11 && toty.some(p => p.id === player.id)) totyComps[labelFor(compKey)] = true;
    });

    // Top scorer in league (Golden Boot winner)
    const goldenBoot = [...allPlayers]
      .map(p => ({ ...p, g: p.stats?.league?.goals || 0 }))
      .filter(p => p.g > 0)
      .sort((a, b) => b.g - a.g)[0];

    return {
      team, player, season: career.season || 1, year: career.currentYear || 2026,
      finalRank, goals, assists, avgRating, matches: seasonMatches.length,
      trophies, mvpComps, totyComps, goldenBoot,
    };
  }, [open, career, intlPools]);

  // Confetti control — re-burst when modal opens with trophies, and can retrigger
  const [confettiKey, setConfettiKey] = useState(0);
  useEffect(() => {
    if (open && data?.trophies?.length > 0) {
      setConfettiKey(k => k + 1);
    }
  }, [open, data?.trophies?.length]);

  // Share flow — use Web Share API when supported, fall back to download
  const [shareState, setShareState] = useState('idle'); // idle | sharing | shared | downloaded
  const canNativeShare = useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.canShare) return false;
    try {
      const probe = new File([new Blob([''], { type: 'image/png' })], 'probe.png', { type: 'image/png' });
      return navigator.canShare({ files: [probe] });
    } catch { return false; }
  }, []);

  if (!data) return null;

  const handleShare = async () => {
    setShareState('sharing');
    try {
      const res = await shareSeasonRecap({
        player: data.player, team: data.team,
        season: data.season, year: data.year,
        finalRank: data.finalRank, trophies: data.trophies,
        stats: { matches: data.matches, goals: data.goals, assists: data.assists, avgRating: data.avgRating },
        mvpComps: data.mvpComps, totyComps: data.totyComps,
      });
      setShareState(res.via === 'webshare' ? 'shared' : 'downloaded');
    } catch (e) {
      console.error('Season recap share failed', e);
      setShareState('idle');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-[#0A0A0C] border-[#D4A933]/50 text-white max-w-2xl p-0 overflow-hidden relative" data-testid="season-end-modal">
        {/* Confetti burst — only when trophies were won */}
        {data.trophies.length > 0 && (
          <ConfettiBurst trigger={confettiKey} count={110} origin="top" zIndex={70} />
        )}
        {/* Cinematic header */}
        <div className="relative px-7 pt-7 pb-5 bg-gradient-to-br from-[#121216] via-[#1a1a22] to-[#0A0A0C] border-b border-[#D4A933]/30">
          <div className="absolute inset-0 opacity-20" style={{
            background: `radial-gradient(ellipse at 50% 0%, rgba(212,169,51,0.4) 0%, transparent 70%)`
          }} />
          <DialogHeader className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={22} className="text-[#D4A933]" weight="fill" />
              <span className="font-heading font-black tracking-[0.3em] text-[11px] text-[#D4A933] uppercase">Season {data.season} Recap</span>
            </div>
            <DialogTitle className="font-heading font-black uppercase tracking-wide text-3xl">
              {data.finalRank === 1 ? 'CHAMPIONS.' : data.trophies.length > 0 ? 'WHAT A SEASON.' : 'SEASON COMPLETE'}
            </DialogTitle>
            <p className="text-[#A0A0A5] text-sm mt-1">{data.player.name} · {data.team.name} · Finished #{data.finalRank}</p>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* Headline stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Apps', value: data.matches, color: '#4DA3FF' },
              { label: 'Goals', value: data.goals, color: '#32D74B' },
              { label: 'Assists', value: data.assists, color: '#D4A933' },
              { label: 'Rating', value: data.avgRating.toFixed(2), color: '#FFD700' },
            ].map(s => (
              <div key={s.label} className="bg-[#121216] border border-[#2C2C35] rounded-sm p-3 text-center">
                <p className="font-heading font-black text-3xl" style={{ color: s.color }}>{s.value}</p>
                <p className="text-[10px] text-[#7A8FAB] uppercase tracking-widest font-bold mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Trophies */}
          <div>
            <p className="text-[11px] text-[#D4A933] uppercase tracking-widest font-bold mb-2 flex items-center gap-2">
              <Trophy size={12} weight="fill" /> Silverware
            </p>
            {data.trophies.length === 0 ? (
              <div className="bg-[#121216] border border-[#2C2C35] rounded-sm p-3 text-[#7A8FAB] text-sm italic">No trophies this season — rebuild incoming.</div>
            ) : (
              <div className="space-y-1.5">
                {data.trophies.map((t, i) => (
                  <div key={i} className="bg-[#121216] border border-[#2C2C35] rounded-sm p-2.5 flex items-center gap-2.5" style={{ borderLeftColor: t.color, borderLeftWidth: 3 }} data-testid={`season-trophy-${i}`}>
                    <Trophy size={16} style={{ color: t.color }} weight="fill" />
                    <span className="font-heading font-black text-sm uppercase tracking-wide">{t.label}</span>
                    <span className="ml-auto text-xl">{t.icon}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Individual awards */}
          {(Object.keys(data.mvpComps).length > 0 || Object.keys(data.totyComps).length > 0) && (
            <div>
              <p className="text-[11px] text-[#FFD700] uppercase tracking-widest font-bold mb-2 flex items-center gap-2">
                <Star size={12} weight="fill" /> Individual Awards
              </p>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(data.mvpComps).map(k => (
                  <Badge key={'m'+k} className="bg-[#FFD700] text-black font-heading font-black tracking-wider" data-testid={`season-mvp-${k}`}>
                    <Medal size={10} weight="fill" className="mr-1" /> {k} MVP
                  </Badge>
                ))}
                {Object.keys(data.totyComps).map(k => (
                  <Badge key={'t'+k} className="bg-[#32D74B] text-black font-heading font-black tracking-wider" data-testid={`season-toty-${k}`}>
                    <Star size={10} weight="fill" className="mr-1" /> {k} TOTY
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Golden Boot winner */}
          {data.goldenBoot && (
            <div className="bg-gradient-to-r from-[#D4A933]/20 via-[#D4A933]/5 to-transparent border border-[#D4A933]/40 rounded-sm p-2.5" data-testid="season-golden-boot">
              <p className="text-[10px] text-[#D4A933] uppercase tracking-widest font-bold mb-1">🥇 Golden Boot Winner</p>
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-sm flex-1 truncate">{data.goldenBoot.name}</span>
                <span className="text-[#A0A0A5] text-xs truncate">{data.goldenBoot.teamName}</span>
                <span className="font-mono font-black text-[#FFD700] text-lg">{data.goldenBoot.g}</span>
                <span className="text-[10px] text-[#7A8FAB] font-bold">GOALS</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex gap-2 p-5 border-t border-[#2C2C35] bg-[#121216]">
          <Button
            onClick={handleShare}
            disabled={shareState === 'sharing'}
            variant="outline"
            className="flex-1 bg-transparent border-[#D4A933] text-[#D4A933] hover:bg-[#D4A933]/10 font-heading font-bold uppercase tracking-wider disabled:opacity-60"
            data-testid="season-share-btn"
          >
            {shareState === 'sharing' ? (
              <>Generating…</>
            ) : shareState === 'shared' ? (
              <><Check size={14} weight="bold" className="mr-1.5" /> Shared</>
            ) : shareState === 'downloaded' ? (
              <><Check size={14} weight="bold" className="mr-1.5" /> Saved · Tap to re-share</>
            ) : (
              <>
                <ShareNetwork size={14} weight="bold" className="mr-1.5" />
                {canNativeShare ? 'Share Story Card' : 'Download Story Card'}
              </>
            )}
          </Button>
          <Button onClick={onContinue} className="flex-1 bg-[#1A9E5E] hover:bg-[#0F7D4B] text-white font-heading font-bold uppercase tracking-wider" data-testid="season-continue-btn">
            Next Season <ArrowRight size={14} weight="bold" className="ml-1.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
